import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, FileText, Loader2, ShieldAlert, Upload, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SelectNative } from "../components/ui/select-native";
import { fetchAssetTypes, importCAS, previewCAS } from "../lib/api";
import { normalizeDetail } from "../lib/errors";

/** Full CAS-import wizard. Renders itself as a modal — parent controls open/close.
 *
 * Three stages internally:
 *   1) `upload`    → form (PDF + PAN + DOB)
 *   2) `preview`   → parsed schemes + name-match check + per-row selection
 *   3) `success`   → counts of created / updated / skipped
 *
 * The raw PDF never leaves the request; the parent's `onImported()` is called after Save so
 * the NetWorthPage can refresh its asset list.
 */
export function CasImportDialog({ open, onClose, onImported }) {
  const [stage, setStage] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [pan, setPan] = useState("");
  const [dob, setDob] = useState("");
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]); // editable copy of preview.selections
  const [ackNameMismatch, setAckNameMismatch] = useState(false);
  const [result, setResult] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);

  if (!open) return null;

  function reset() {
    setStage("upload");
    setBusy(false);
    setFile(null);
    setPan("");
    setDob("");
    setPreview(null);
    setRows([]);
    setAckNameMismatch(false);
    setResult(null);
  }

  function handleClose() {
    if (busy) return;
    reset();
    onClose?.();
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a CAS PDF.");
      return;
    }
    setBusy(true);
    try {
      const [types, pv] = await Promise.all([
        fetchAssetTypes(),
        previewCAS({ file, pan, dob }),
      ]);
      // Filter to Investments category so the dropdown stays scoped to sensible options.
      setAssetTypes(types.filter((t) => t.category === "Investments"));
      setPreview(pv);
      setRows(
        pv.selections.map((s) => ({
          ...s,
          include: true, // opt-out by default
          nameOverride: s.scheme,
        }))
      );
      setStage("preview");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns {code, message} for CAS-specific errors so we surface the message directly.
      if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(normalizeDetail(detail, "Couldn't parse the CAS."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleImport() {
    if (preview.name_mismatch && !ackNameMismatch) {
      toast.error(
        `The CAS is in the name "${preview.investor_name}" but your profile says "${preview.profile_name ?? ""}". Confirm below to proceed.`
      );
      return;
    }
    const anyIncluded = rows.some((r) => r.include);
    if (!anyIncluded) {
      toast.error("Select at least one scheme to import.");
      return;
    }
    setBusy(true);
    try {
      const data = await importCAS({
        preview_id: preview.preview_id,
        acknowledged_name_mismatch: ackNameMismatch,
        selections: rows.map((r) => ({
          key: r.key,
          include: !!r.include,
          asset_type_id: r.asset_type_id,
          name: r.nameOverride,
        })),
      });
      setResult(data);
      setStage("success");
      onImported?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(normalizeDetail(detail, "Couldn't save the CAS import."));
      }
    } finally {
      setBusy(false);
    }
  }

  function updateRow(key, patch) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="cas-dialog"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} aria-hidden />
      <div className="relative mt-8 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur">
          <div>
            <h3 className="text-lg font-semibold text-text">Import CAS statement</h3>
            <p className="text-xs text-muted">
              Upload a Consolidated Account Statement from CAMS or KFintech. We&apos;ll auto-populate
              your mutual-fund holdings — nothing is saved until you review and confirm.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6">
          {stage === "upload" && (
            <UploadStage
              file={file}
              setFile={setFile}
              pan={pan}
              setPan={setPan}
              dob={dob}
              setDob={setDob}
              busy={busy}
              onSubmit={handleUpload}
            />
          )}
          {stage === "preview" && preview && (
            <PreviewStage
              preview={preview}
              rows={rows}
              assetTypes={assetTypes}
              updateRow={updateRow}
              ack={ackNameMismatch}
              setAck={setAckNameMismatch}
              busy={busy}
              onBack={() => setStage("upload")}
              onImport={handleImport}
            />
          )}
          {stage === "success" && result && (
            <SuccessStage result={result} onClose={handleClose} />
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Upload stage --------------------------------- */

function UploadStage({ file, setFile, pan, setPan, dob, setDob, busy, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      <label
        htmlFor="cas-file-input"
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gold/40 bg-elevated/40 p-8 text-center transition hover:border-gold hover:bg-elevated/70"
        data-testid="cas-upload-drop"
      >
        {file ? (
          <>
            <FileText className="h-8 w-8 text-gold" />
            <div className="text-sm text-text">{file.name}</div>
            <div className="text-xs text-muted">
              {(file.size / 1024).toFixed(1)} KB — click to choose a different file
            </div>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-gold" />
            <div className="text-sm text-text">Click to choose your CAS PDF</div>
            <div className="text-xs text-muted">
              Up to 10 MB · encrypted with your PAN — download from{" "}
              <a
                href="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement"
                target="_blank"
                rel="noreferrer"
                className="text-gold underline"
              >
                CAMS
              </a>{" "}
              or{" "}
              <a
                href="https://mfs.kfintech.com/investor/General/ConsolidatedAccountStatement"
                target="_blank"
                rel="noreferrer"
                className="text-gold underline"
              >
                KFintech
              </a>
            </div>
          </>
        )}
        <input
          id="cas-file-input"
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="hidden"
          data-testid="cas-file-input"
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cas-pan">PAN *</Label>
          <Input
            id="cas-pan"
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase())}
            placeholder="AAAAA9999A"
            maxLength={10}
            required
            data-testid="cas-pan-input"
          />
          <p className="text-[11px] text-muted">Used only to unlock the PDF — never sent to third parties.</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cas-dob">Date of birth (if CAS uses PAN + DOB)</Label>
          <Input
            id="cas-dob"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            data-testid="cas-dob-input"
          />
          <p className="text-[11px] text-muted">Optional — we&apos;ll try PAN first. Not stored.</p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-gold/20 bg-gold/5 p-3 text-xs text-muted">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
        <div>
          Your PDF is decrypted in-memory and <strong>never persisted</strong>. Only the parsed
          fund names, units and values are stored — you can drop them from the preview screen
          before saving.
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={busy || !file} data-testid="cas-parse-btn" className="gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Reading CAS…" : "Parse statement"}
        </Button>
      </div>
    </form>
  );
}

/* --------------------------------- Preview stage --------------------------------- */

function formatINR(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
}

function PreviewStage({ preview, rows, assetTypes, updateRow, ack, setAck, busy, onBack, onImport }) {
  const totalSelected = rows
    .filter((r) => r.include)
    .reduce((s, r) => s + Number(r.value || 0), 0);
  const dupCount = rows.filter((r) => r.duplicate_status === "update").length;
  const newCount = rows.filter((r) => r.duplicate_status === "new").length;

  return (
    <div className="flex flex-col gap-5">
      {/* Header summary */}
      <div className="rounded-xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Investor</div>
            <div className="mt-1 font-medium text-text">{preview.investor_name || "—"}</div>
            <div className="text-[11px] text-muted">PAN {preview.pan_masked}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Statement period</div>
            <div className="mt-1 font-medium text-text">
              {preview.statement_from || "?"} → {preview.statement_to || "?"}
            </div>
            <div className="text-[11px] text-muted">{preview.cas_type}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Reported value</div>
            <div className="mt-1 text-xl font-semibold text-gold">₹ {formatINR(preview.total_value)}</div>
            <div className="text-[11px] text-muted">{rows.length} schemes found</div>
          </div>
        </div>
      </div>

      {preview.name_mismatch && (
        <NameMismatchBanner preview={preview} ack={ack} setAck={setAck} />
      )}

      <ParserDiagnostics preview={preview} rows={rows} />

      {preview.parse_warnings?.length > 0 && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-200">
          <div className="mb-1 font-medium">Parser warnings ({preview.parse_warnings.length})</div>
          <ul className="ml-4 list-disc space-y-0.5">
            {preview.parse_warnings.slice(0, 5).map((w, i) => (
              <li key={`warning-${i}-${w.slice(0, 40)}`}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-3 text-muted">
          <span>
            <span className="text-text">{rows.filter((r) => r.include).length}</span> /{" "}
            {rows.length} selected
          </span>
          <span className="text-emerald-400">{newCount} new</span>
          <span className="text-gold">{dupCount} existing → update</span>
        </div>
        <div className="text-sm text-muted">
          Import total{" "}
          <span className="font-semibold text-gold">₹ {formatINR(totalSelected)}</span>
        </div>
      </div>

      <div className="grid gap-2">
        {rows.map((r) => (
          <SchemeRow key={r.key} row={r} assetTypes={assetTypes} update={(patch) => updateRow(r.key, patch)} />
        ))}
      </div>

      <div className="flex justify-between gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={busy}>
          Back
        </Button>
        <Button
          type="button"
          onClick={onImport}
          disabled={busy}
          data-testid="cas-import-btn"
          className="gap-2"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Saving…" : `Save ${rows.filter((r) => r.include).length} schemes`}
        </Button>
      </div>
    </div>
  );
}

function NameMismatchBanner({ preview, ack, setAck }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-400/40 bg-amber-400/5 p-4">
      <div className="flex items-start gap-2 text-sm text-amber-200">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          <div className="font-medium">Name mismatch</div>
          <div className="text-xs">
            CAS name: <strong>{preview.investor_name}</strong>
            <br />
            Your profile: <strong>{preview.profile_name || "—"}</strong>
            <br />
            Similarity: {preview.name_similarity?.toFixed(0)}% — please confirm this CAS belongs
            to you before proceeding.
          </div>
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-amber-100">
        <input
          type="checkbox"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
          data-testid="cas-name-ack"
          className="h-4 w-4 rounded border-amber-400/40"
        />
        Yes, this CAS is mine.
      </label>
    </div>
  );
}

/** Compact "what the parser saw" panel. When the extracted row count doesn't match
 * what the user expected (e.g. equities showing 0 for an NSDL demat statement), this
 * is the first place to look. Falls back gracefully when diagnostics is missing. */
function ParserDiagnostics({ preview, rows }) {
  const [open, setOpen] = useState(false);
  const diag = preview?.diagnostics;
  const counts = rows.reduce(
    (acc, r) => ({ ...acc, [r.holding_type]: (acc[r.holding_type] || 0) + 1 }),
    {}
  );
  const totalExtracted = rows.length;

  // Sum equity + bond counts across all NSDL accounts to decide whether we should
  // show a proactive "missing extractions" hint.
  const totalEqCounted = diag?.accounts?.reduce((s, a) => s + (a.equity_count || 0), 0) ?? 0;
  const totalMFCounted = diag?.accounts?.reduce((s, a) => s + (a.mutual_fund_count || 0), 0) ?? 0;
  const totalBondCounted = diag?.accounts?.reduce((s, a) => s + (a.bond_count || 0), 0) ?? 0;

  return (
    <div className="rounded-lg border border-border bg-elevated/40" data-testid="cas-diagnostics">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-xs text-muted hover:text-text"
      >
        <span className="uppercase tracking-widest">Parser diagnostics</span>
        <span>
          {totalExtracted} extracted · MF {counts.mutual_fund ?? 0} · Equity{" "}
          {counts.equity ?? 0} · Bond {counts.bond ?? 0}
          {open ? " ▲" : " ▼"}
        </span>
      </button>
      {open && (
        <div className="border-t border-border p-4 text-xs text-muted">
          <div className="mb-2">
            <span className="text-text">File type:</span> {diag?.file_type || "unknown"} ·{" "}
            <span className="text-text">CAMS folios:</span> {diag?.cams_folio_count ?? 0} ·{" "}
            <span className="text-text">NSDL accounts:</span> {diag?.nsdl_account_count ?? 0}
          </div>
          {(diag?.supplementary_added > 0 || diag?.supplementary_corrected > 0) && (
            <div className="mb-3 rounded-md border border-emerald-400/30 bg-emerald-400/5 p-2.5 text-emerald-200">
              <strong>Text-scan recovery:</strong> found{" "}
              <span className="font-semibold">{diag.supplementary_added}</span> holdings the
              primary parser missed
              {diag.supplementary_corrected > 0 && (
                <> and corrected <span className="font-semibold">{diag.supplementary_corrected}</span>{" "}
                misclassified rows (e.g. bonds tagged as equities)</>
              )}
              .
            </div>
          )}
          {diag?.accounts?.length > 0 && (
            <div className="mb-3 overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-[10px] uppercase tracking-widest text-muted">
                  <tr>
                    <th className="pr-3 py-1">Account</th>
                    <th className="pr-3 py-1">Type</th>
                    <th className="pr-3 py-1">DP ID</th>
                    <th className="pr-3 py-1">MF</th>
                    <th className="pr-3 py-1">Equity</th>
                    <th className="pr-3 py-1">Bond</th>
                  </tr>
                </thead>
                <tbody>
                  {diag.accounts.map((a, i) => (
                    <tr key={`account-${a.dp_id_masked || i}-${a.type}`} className="border-t border-border/60">
                      <td className="pr-3 py-1.5 text-text">{a.name}</td>
                      <td className="pr-3 py-1.5">{a.type}</td>
                      <td className="pr-3 py-1.5 font-mono text-[11px]">{a.dp_id_masked}</td>
                      <td className="pr-3 py-1.5">{a.mutual_fund_count}</td>
                      <td className="pr-3 py-1.5">{a.equity_count}</td>
                      <td className="pr-3 py-1.5">{a.bond_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(totalEqCounted === 0 && totalMFCounted > 0) && (
            <div className="rounded-md border border-amber-400/30 bg-amber-400/5 p-3 text-amber-200">
              <strong>Heads up:</strong> your statement summary shows an Equity section but
              casparser extracted 0 direct-equity rows. This can happen on some NSDL
              layouts casparser doesn&apos;t match yet, or the shares may be under &quot;Others&quot;
              (which the parser doesn&apos;t support). Please add those rows manually for now,
              or share a screenshot of the equity section and we&apos;ll extend the parser.
            </div>
          )}
          {(totalBondCounted === 0 && (totalMFCounted > 0 || totalEqCounted > 0)) && (
            <div className="mt-2 rounded-md border border-purple-400/30 bg-purple-400/5 p-3 text-purple-200">
              <strong>Note:</strong> no Debts / Bonds were extracted. If your statement has them
              under &quot;Debts / Others&quot;, add them via <em>Add asset → Bonds</em>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SchemeRow({ row, assetTypes, update }) {
  const bgClass = row.duplicate_status === "update"
    ? "border-gold/30 bg-gold/[0.04]"
    : "border-border bg-elevated/30";
  return (
    <div
      className={`grid gap-3 rounded-lg border p-3 sm:grid-cols-[auto_1fr_auto] ${bgClass}`}
      data-testid={`cas-row-${row.key}`}
    >
      <input
        type="checkbox"
        checked={row.include}
        onChange={(e) => update({ include: e.target.checked })}
        className="mt-2 h-4 w-4 rounded border-border"
        aria-label={`Include ${row.scheme}`}
        data-testid={`cas-row-check-${row.key}`}
      />
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-medium text-text">{row.scheme}</div>
          {row.holding_type === "equity" && (
            <span className="rounded-full border border-sky-400/40 bg-sky-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-sky-300">
              equity
            </span>
          )}
          {row.holding_type === "bond" && (
            <span className="rounded-full border border-purple-400/40 bg-purple-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-purple-300">
              bond
            </span>
          )}
          {row.duplicate_status === "update" && (
            <span className="rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
              existing · will update
            </span>
          )}
          {row.duplicate_status === "new" && (
            <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-300">
              new
            </span>
          )}
          {row.reconciliation_warning && (
            <span
              className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-300"
              title={row.reconciliation_warning}
            >
              reconciliation
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted">
          {row.amc} · Folio {row.folio_masked || "—"} · ISIN {row.isin || "—"}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SmallField
            label={row.holding_type === "equity" ? "Shares" : row.holding_type === "bond" ? "Bonds" : "Units"}
            value={row.units?.toFixed(row.holding_type === "mutual_fund" ? 3 : 0)}
          />
          <SmallField
            label={row.holding_type === "mutual_fund" ? "NAV" : "Price"}
            value={`₹ ${formatINR(row.nav)}`}
          />
          <SmallField label="Cost" value={`₹ ${formatINR(row.cost)}`} />
          <SmallField label="Value" value={`₹ ${formatINR(row.value)}`} bold />
        </div>
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          <SelectNative
            value={row.asset_type_id}
            onChange={(e) => update({ asset_type_id: e.target.value })}
            data-testid={`cas-row-type-${row.key}`}
          >
            {assetTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.item_type}
              </option>
            ))}
          </SelectNative>
          <Input
            value={row.nameOverride}
            onChange={(e) => update({ nameOverride: e.target.value })}
            placeholder="Custom name (optional)"
          />
        </div>
      </div>
      <div className="hidden text-right text-xs text-muted sm:block">
        <div className="text-[10px] uppercase tracking-widest">as of</div>
        <div>{row.valuation_date || "—"}</div>
      </div>
    </div>
  );
}

function SmallField({ label, value, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
      <div className={`text-sm ${bold ? "font-semibold text-gold" : "text-text"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

/* --------------------------------- Success stage --------------------------------- */

function SuccessStage({ result, onClose }) {
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-400/10">
        <Check className="h-6 w-6 text-emerald-400" />
      </div>
      <h4 className="font-serif text-2xl text-text">CAS imported</h4>
      <p className="text-sm text-muted">
        {result.created} new asset{result.created === 1 ? "" : "s"} created ·{" "}
        {result.updated} existing asset{result.updated === 1 ? "" : "s"} updated ·{" "}
        {result.skipped} skipped
      </p>
      <Button onClick={onClose} data-testid="cas-close-btn">
        Done
      </Button>
    </div>
  );
}

export default CasImportDialog;
